import React, { useState, useMemo } from 'react';
import { Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { getRoleService } from '@/services/roleService';
import { useDataSource } from '@/contexts/DataSourceContext';
import { useTimesheetContext } from '@/contexts/TimesheetContext';
import type { CreateRoleDto } from '@/types/role';
import { toast } from 'sonner';

interface RoleDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

export const RoleDialog: React.FC<RoleDialogProps> = ({
  open,
  onOpenChange,
  onSuccess,
}) => {
  const { getScreenMode } = useDataSource();
  const { currentEmployee } = useTimesheetContext();
  const screenMode = getScreenMode('roles-rates');
  const roleService = useMemo(() => getRoleService(screenMode), [screenMode]);
  
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [includeInitialRate, setIncludeInitialRate] = useState(false);
  const [billingRate, setBillingRate] = useState('');
  const [costRate, setCostRate] = useState('');
  const [currency, setCurrency] = useState('USD');
  const [effectiveFrom, setEffectiveFrom] = useState(
    new Date().toISOString().split('T')[0]
  );
  const [isSaving, setIsSaving] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim()) {
      toast.error('Role name is required');
      return;
    }

    setIsSaving(true);

    try {
      const dto: CreateRoleDto = {
        name: name.trim(),
        description: description.trim() || undefined,
      };

      if (includeInitialRate) {
        dto.initialRate = {
          billingRate: billingRate ? parseFloat(billingRate) : undefined,
          costRate: costRate ? parseFloat(costRate) : undefined,
          currency,
          effectiveFrom,
        };
      }

      await roleService.createRole(dto, currentEmployee.id);
      
      // Reset form
      setName('');
      setDescription('');
      setIncludeInitialRate(false);
      setBillingRate('');
      setCostRate('');
      setEffectiveFrom(new Date().toISOString().split('T')[0]);
      
      onSuccess();
    } catch (err) {
      console.error('Error creating role:', err);
      toast.error('Failed to create role');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Create New Role</DialogTitle>
            <DialogDescription>
              Define a new organizational role and optionally set its initial rates
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Role Name */}
            <div className="space-y-2">
              <Label htmlFor="name">
                Role Name <span className="text-destructive">*</span>
              </Label>
              <Input
                id="name"
                placeholder="e.g., Senior Developer"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                placeholder="Brief description of this role..."
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
              />
            </div>

            {/* Include Initial Rate Checkbox */}
            <div className="flex items-center space-x-2 pt-2">
              <Checkbox
                id="includeRate"
                checked={includeInitialRate}
                onCheckedChange={(checked) => setIncludeInitialRate(checked as boolean)}
              />
              <Label
                htmlFor="includeRate"
                className="text-sm font-normal cursor-pointer"
              >
                Set initial rates now
              </Label>
            </div>

            {/* Initial Rate Fields */}
            {includeInitialRate && (
              <div className="space-y-4 pl-6 border-l-2 border-muted">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="billingRate">Billing Rate ($/hr)</Label>
                    <Input
                      id="billingRate"
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder="150.00"
                      value={billingRate}
                      onChange={(e) => setBillingRate(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="costRate">Cost Rate ($/hr)</Label>
                    <Input
                      id="costRate"
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder="85.00"
                      value={costRate}
                      onChange={(e) => setCostRate(e.target.value)}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="effectiveFrom">Effective From</Label>
                  <Input
                    id="effectiveFrom"
                    type="date"
                    value={effectiveFrom}
                    onChange={(e) => setEffectiveFrom(e.target.value)}
                  />
                </div>

                {billingRate && costRate && (
                  <div className="text-sm text-muted-foreground">
                    Margin: {' '}
                    <span className="font-medium">
                      {(
                        ((parseFloat(billingRate) - parseFloat(costRate)) /
                          parseFloat(billingRate)) *
                        100
                      ).toFixed(1)}
                      %
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSaving}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSaving}>
              {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Create Role
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
